import { useQuery } from "@tanstack/react-query";

import { apiBaseConfigured, applicantApi, type SessionStatus, type SessionStatusName } from "@/lib/api";

/**
 * Submitted, whether or not every vouch is back: a submit with a vouch still
 * outstanding leaves the session in `awaiting_vouches`, and the voucher's
 * confirmation then moves it to `completed` (e2e J1 asserts both).
 */
const SUBMITTED: readonly SessionStatusName[] = ["awaiting_vouches", "completed"];
/** Can no longer be submitted. */
const ENDED: readonly SessionStatusName[] = ["expired", "declined"];

/**
 * The live demo run's applicant session, read by the Feather preview bar and
 * the ATS view. Demo-only (both callers are /feather pages).
 *
 * One react-query entry, so the bar and the page share a single poll. It
 * polls every 12s while the tab is visible, the applicant page's cadence, and
 * stops once the session is `completed` or has ended. It keeps polling while
 * `awaiting_vouches`, so the ATS view picks up the vouch coming back.
 */
export function useDemoRunSession(token: string | null | undefined) {
  const enabled = Boolean(token) && apiBaseConfigured();
  const query = useQuery<SessionStatus>({
    queryKey: ["feather-demo", "run-session", token],
    queryFn: () => applicantApi.getSessionStatus(token as string),
    enabled,
    retry: 1,
    staleTime: 10_000,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (status === "completed" || (status && ENDED.includes(status))) return false;
      return document.visibilityState === "visible" ? 12_000 : false;
    },
    refetchIntervalInBackground: false,
  });
  const status = query.data?.status;
  return {
    session: query.data ?? null,
    submitted: Boolean(status && SUBMITTED.includes(status)) || Boolean(query.data?.submitted_at),
    ended: Boolean(status && ENDED.includes(status)),
    loading: enabled && query.isLoading,
    error: (query.error as Error) ?? null,
  };
}
